(function () {
  'use strict';

  function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
  }

  function easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  function lerpObject(from, to, t, keys) {
    var out = {};
    keys.forEach(function (key) {
      out[key] = lerp(from[key], to[key], t);
    });
    return out;
  }

  function animateBetween(renderFn, durationMs, onDone) {
    var start = performance.now();
    var dur = Math.max(durationMs, 1);

    function frame(now) {
      var t = easeOutCubic(clamp((now - start) / dur, 0, 1));
      renderFn(t);
      if (t < 1) {
        requestAnimationFrame(frame);
      } else if (onDone) {
        onDone();
      }
    }

    requestAnimationFrame(frame);
  }

  function TriggeredScenes(options) {
    var track = options.track;
    var stepCount = Math.max(2, options.stepCount || 2);
    var wheelsToAdvance = options.wheelsToAdvance || 3;
    var animationMs = options.animationMs || 500;
    var reducedMotion = !!options.reducedMotion;
    var enabled = options.enabled || function () { return true; };
    var lockSceneWhenPinned = !!options.lockSceneWhenPinned;
    var shouldAnimate = options.shouldAnimate || function () { return true; };
    var getState = options.getState;
    var applyState = options.applyState;
    var lerpStates = options.lerpStates;

    var currentScene = 0;
    var wheelTicks = 0;
    var isActive = false;
    var isAnimating = false;
    var touchStartY = 0;
    var touchAccum = 0;
    var destroyed = false;
    var mobileMq = window.matchMedia('(max-width: 767px)');

    function viewportHeight() {
      var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      var barH = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--lp-pdp-sticky-bar-height')
      ) || 0;
      return Math.max(0, vh - barH);
    }

    function advanceThreshold() {
      return mobileMq.matches ? 1 : wheelsToAdvance;
    }

    function maxScene() {
      return stepCount - 1;
    }

    function isPinned() {
      var rect = track.getBoundingClientRect();
      var vh = viewportHeight();
      var scrollable = track.offsetHeight - vh;
      return scrollable > 0 && rect.top <= 2 && rect.bottom >= vh - 2;
    }

    function scrollToScene(scene, smooth) {
      var vh = viewportHeight();
      var scrollable = track.offsetHeight - vh;
      if (scrollable <= 0) return;
      var p = maxScene() <= 0 ? 0 : scene / maxScene();
      var top = track.getBoundingClientRect().top + window.scrollY + p * scrollable;
      window.scrollTo({
        top: top,
        behavior: smooth && !reducedMotion ? 'smooth' : 'auto'
      });
    }

    function goToScene(next, animate) {
      next = clamp(next, 0, maxScene());
      if (next === currentScene) return;

      var from = currentScene;
      currentScene = next;
      wheelTicks = 0;

      var canAnimate = animate && !reducedMotion && shouldAnimate(from, currentScene);

      if (!canAnimate) {
        applyState(getState(currentScene), true);
        scrollToScene(currentScene, false);
        return;
      }

      isAnimating = true;
      var fromState = getState(from);
      var toState = getState(currentScene);

      animateBetween(function (t) {
        applyState(lerpStates(fromState, toState, t), false);
      }, animationMs, function () {
        applyState(getState(currentScene), true);
        isAnimating = false;
      });

      scrollToScene(currentScene, !lockSceneWhenPinned);
    }

    function registerWheelDelta(delta) {
      if (!enabled() || !isActive || isAnimating) return;
      if (!isPinned()) return;

      var dir = delta > 0 ? 1 : -1;
      if (dir > 0 && currentScene >= maxScene()) return;
      if (dir < 0 && currentScene <= 0) return;

      if (wheelTicks !== 0 && Math.sign(wheelTicks) !== dir) wheelTicks = 0;
      wheelTicks += dir;

      if (Math.abs(wheelTicks) >= advanceThreshold()) {
        goToScene(currentScene + dir, true);
      }
    }

    function onWheel(e) {
      if (!enabled() || !isActive || isAnimating) return;
      if (!isPinned()) return;
      if (currentScene >= maxScene() && e.deltaY > 0) return;
      if (currentScene <= 0 && e.deltaY < 0) return;

      e.preventDefault();
      registerWheelDelta(e.deltaY);
    }

    function onTouchStart(e) {
      if (!enabled() || !isActive) return;
      touchStartY = e.touches[0].clientY;
      touchAccum = 0;
    }

    function onTouchMove(e) {
      if (!enabled() || !isActive || isAnimating) return;
      if (!isPinned()) return;

      e.preventDefault();

      var dy = touchStartY - e.touches[0].clientY;
      touchStartY = e.touches[0].clientY;
      touchAccum += dy;

      if (Math.abs(touchAccum) < 40) return;

      var dir = touchAccum > 0 ? 1 : -1;
      touchAccum = 0;
      if (dir > 0 && currentScene >= maxScene()) return;
      if (dir < 0 && currentScene <= 0) return;

      if (wheelTicks !== 0 && Math.sign(wheelTicks) !== dir) wheelTicks = 0;
      wheelTicks += dir;

      if (Math.abs(wheelTicks) >= advanceThreshold()) {
        goToScene(currentScene + dir, true);
      }
    }

    function syncSceneFromScroll() {
      if (!enabled() || isAnimating || destroyed) return;
      var mobileView = mobileMq.matches;
      if (lockSceneWhenPinned && isPinned() && !mobileView) return;
      var vh = viewportHeight();
      var scrollable = track.offsetHeight - vh;
      if (scrollable <= 0) return;
      var rect = track.getBoundingClientRect();
      var raw = clamp(-rect.top / scrollable, 0, 1);
      var scene = Math.round(raw * maxScene());
      if (scene !== currentScene) {
        currentScene = scene;
        wheelTicks = 0;
        applyState(getState(currentScene), true);
      }
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;
      observer.disconnect();
      window.removeEventListener('scroll', syncSceneFromScroll);
      window.removeEventListener('wheel', onWheel);
      track.removeEventListener('touchstart', onTouchStart);
      track.removeEventListener('touchmove', onTouchMove);
    }

    var observer = new IntersectionObserver(function (entries) {
      isActive = entries[0].isIntersecting;
      if (isActive && enabled()) {
        syncSceneFromScroll();
        applyState(getState(currentScene), true);
      }
    }, { threshold: 0 });

    observer.observe(track);
    window.addEventListener('scroll', syncSceneFromScroll, { passive: true });
    window.addEventListener('wheel', onWheel, { passive: false });
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchmove', onTouchMove, { passive: false });

    applyState(getState(0), true);

    return {
      goToScene: goToScene,
      getCurrentScene: function () { return currentScene; },
      isAnimating: function () { return isAnimating; },
      destroy: destroy
    };
  }

  window.LpPdpTriggeredScenes = {
    create: function (options) {
      return new TriggeredScenes(options);
    },
    viewportHeight: function () {
      if (typeof window.lpPdpViewportHeight === 'function') {
        return window.lpPdpViewportHeight();
      }
      var vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      var barH = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--lp-pdp-sticky-bar-height')
      ) || 0;
      return Math.max(0, vh - barH);
    },
    animateBetween: animateBetween,
    lerp: lerp,
    lerpObject: lerpObject,
    easeOutCubic: easeOutCubic
  };
})();
